import React from 'react';
import { useParams, Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const ServiceDetail = () => {
  const { serviceId } = useParams();

  const services = {
    'kitchen-renovations': {
      title: 'Kitchen Renovations',
      description: 'Transform your kitchen into the heart of your home with our expert renovation services',
      longDescription: 'Your kitchen is more than just a place to cook – it\'s where families gather, memories are made, and life happens. At TM Construction, we specialize in creating beautiful, functional kitchens that perfectly match your lifestyle and budget. From modern minimalist designs to traditional country kitchens, our team brings expertise in all aspects of kitchen renovation.',
      features: [
        'Custom kitchen design and planning',
        'Quality cabinet installation',
        'Worktop fitting (granite, quartz, laminate)',
        'Plumbing and electrical work',
        'Flooring installation',
        'Tiling and splashbacks',
        'Modern appliance integration',
        'Lighting design and installation'
      ],
      images: [
        'https://images.unsplash.com/photo-1556912173-3bb406ef7e77?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Consultation', desc: 'Discuss your vision, needs, and budget' },
        { step: 'Design', desc: 'Create detailed plans and 3D visualizations' },
        { step: 'Preparation', desc: 'Strip out old kitchen safely and efficiently' },
        { step: 'Installation', desc: 'Professional fitting of all components' },
        { step: 'Finishing', desc: 'Final touches and quality checks' }
      ],
      icon: 'ChefHat'
    },
    'bathroom-installations': {
      title: 'Bathroom Installations',
      description: 'Create your perfect sanctuary with our luxury bathroom installation services',
      longDescription: 'Transform your bathroom into a luxurious retreat with TM Construction. Whether you\'re looking for a sleek modern wet room, a traditional family bathroom, or an elegant ensuite, we have the expertise to deliver exceptional results. Our team handles everything from initial design through to final installation.',
      features: [
        'Complete bathroom design service',
        'Suite installation (bath, shower, toilet, basin)',
        'Wet room and walk-in shower installation',
        'Underfloor heating',
        'Wall and floor tiling',
        'Plumbing and drainage',
        'Electrical work and lighting',
        'Ventilation systems'
      ],
      images: [
        'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Planning', desc: 'Design your ideal bathroom layout' },
        { step: 'Removal', desc: 'Careful removal of existing bathroom' },
        { step: 'First Fix', desc: 'Plumbing and electrical rough-in' },
        { step: 'Installation', desc: 'Fitting suite, tiling, and finishes' },
        { step: 'Completion', desc: 'Final testing and handover' }
      ],
      icon: 'Bath'
    },
    'extensions-loft-conversions': {
      title: 'Extensions & Loft Conversions',
      description: 'Expand your living space with expertly designed and constructed extensions',
      longDescription: 'Need more space but don\'t want to move? Our extension and loft conversion services provide the perfect solution. We handle everything from planning permission and building regulations through to final completion, creating beautiful new spaces that seamlessly integrate with your existing property.',
      features: [
        'Single and double storey extensions',
        'Loft conversions and dormer additions',
        'Planning permission assistance',
        'Building regulation compliance',
        'Structural engineering',
        'Velux window installation',
        'Insulation and energy efficiency',
        'Full interior finishing'
      ],
      images: [
        'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Survey', desc: 'Assess feasibility and discuss options' },
        { step: 'Planning', desc: 'Submit and obtain necessary permissions' },
        { step: 'Foundation', desc: 'Groundwork and structural work' },
        { step: 'Build', desc: 'Construction of extension structure' },
        { step: 'Finishing', desc: 'Interior completion and decoration' }
      ],
      icon: 'Home'
    },
    'property-refurbishment': {
      title: 'Property Refurbishment',
      description: 'Complete property transformations with attention to every detail',
      longDescription: 'Whether you\'ve purchased a property in need of modernization or want to give your home a complete makeover, our property refurbishment service covers everything. We manage the entire project from start to finish, coordinating all trades and ensuring the highest standards throughout.',
      features: [
        'Complete property renovation',
        'Structural modifications and repairs',
        'Rewiring and electrical upgrades',
        'Plumbing system replacement',
        'Replastering and damp treatment',
        'New flooring throughout',
        'Kitchen and bathroom installation',
        'Decoration and finishing'
      ],
      images: [
        'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Assessment', desc: 'Full property survey and planning' },
        { step: 'Strip Out', desc: 'Remove old fixtures and fittings' },
        { step: 'First Fix', desc: 'Structural, electrical, and plumbing work' },
        { step: 'Second Fix', desc: 'Install new fixtures and fittings' },
        { step: 'Decoration', desc: 'Paint, flooring, and final touches' }
      ],
      icon: 'Hammer'
    },
    'painting-decorating': {
      title: 'Painting & Decorating',
      description: 'Professional painting services using premium quality materials',
      longDescription: 'Give your property a fresh new look with our professional painting and decorating services. Using only premium quality paints and materials, our skilled decorators deliver flawless finishes that transform interiors and exteriors. From single rooms to entire properties, we ensure long-lasting, beautiful results.',
      features: [
        'Interior and exterior painting',
        'Wallpaper removal and hanging',
        'Premium quality paints',
        'Woodwork and trim painting',
        'Ceiling and wall preparation',
        'Feature walls and decorative finishes',
        'Commercial and residential',
        'Minimal disruption service'
      ],
      images: [
        'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&h=600&fit=crop',
        'https://tandmbuilders.co.uk/pro4.1.jpg',
        'https://tandmbuilders.co.uk/pro4.6.jpg'
      ],
      process: [
        { step: 'Consultation', desc: 'Discuss colors and finishes' },
        { step: 'Preparation', desc: 'Protect furniture, fill holes, sand surfaces' },
        { step: 'Priming', desc: 'Apply primer for perfect adhesion' },
        { step: 'Painting', desc: 'Multiple coats for flawless finish' },
        { step: 'Inspection', desc: 'Final checks and touch-ups' }
      ],
      icon: 'PaintBucket'
    },
    'general-maintenance': {
      title: 'General Maintenance',
      description: 'Comprehensive property maintenance to keep your home in perfect condition',
      longDescription: 'Regular maintenance is essential to protect your property investment. Our general maintenance service covers all aspects of property care, from routine repairs to emergency fixes. We provide reliable, professional service for both residential and commercial properties across Suffolk, Essex, and London.',
      features: [
        'Regular maintenance contracts',
        'Emergency repair service',
        'Carpentry and joinery',
        'Plumbing repairs',
        'Electrical maintenance',
        'Guttering and roofing',
        'Garden and outdoor repairs',
        'Commercial property maintenance'
      ],
      images: [
        'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1590698933947-a202b069a861?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Inspection', desc: 'Identify issues and assess requirements' },
        { step: 'Quote', desc: 'Provide detailed cost estimate' },
        { step: 'Schedule', desc: 'Arrange convenient appointment' },
        { step: 'Repair', desc: 'Complete work to high standards' },
        { step: 'Follow-up', desc: 'Ensure complete satisfaction' }
      ],
      icon: 'Wrench'
    },
    'landscaping': {
      title: 'Landscaping',
      description: 'Transform your outdoor spaces with professional landscaping services',
      longDescription: 'Create beautiful outdoor living spaces with our comprehensive landscaping services. From garden design and patio installation to ongoing maintenance, we bring expertise in all aspects of landscaping. Our team works with you to create outdoor areas that complement your property and enhance your lifestyle.',
      features: [
        'Garden design and planning',
        'Patio and decking installation',
        'Fencing and gates',
        'Driveways and pathways',
        'Artificial and natural turf',
        'Garden walls and raised beds',
        'Outdoor lighting',
        'Regular garden maintenance'
      ],
      images: [
        'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Consultation', desc: 'Discuss your vision and requirements' },
        { step: 'Design', desc: 'Create detailed landscape plans' },
        { step: 'Preparation', desc: 'Site clearance and groundwork' },
        { step: 'Installation', desc: 'Build patios, paths, and features' },
        { step: 'Planting', desc: 'Add plants and final touches' }
      ],
      icon: 'Trees'
    },
    'cleaning-services': {
      title: 'Cleaning Services',
      description: 'Professional deep cleaning for construction and renovation projects',
      longDescription: 'After any construction or renovation work, thorough cleaning is essential. Our professional cleaning services ensure your property is spotless and ready to use. We specialize in post-construction cleaning, removing dust, debris, and builder\'s mess to leave your property pristine.',
      features: [
        'Post-construction cleaning',
        'Deep cleaning services',
        'Dust and debris removal',
        'Window cleaning',
        'Floor cleaning and polishing',
        'Kitchen and bathroom sanitizing',
        'Waste removal',
        'Final touches and inspection'
      ],
      images: [
        'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=800&h=600&fit=crop'
      ],
      process: [
        { step: 'Assessment', desc: 'Survey cleaning requirements' },
        { step: 'Planning', desc: 'Create cleaning schedule' },
        { step: 'Deep Clean', desc: 'Remove all dust and debris' },
        { step: 'Detail Work', desc: 'Windows, fixtures, and finishes' },
        { step: 'Final Check', desc: 'Inspection and touch-ups' }
      ],
      icon: 'Sparkles'
    }
  };

  const service = services[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-white pt-56">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button className="bg-orange-600 hover:bg-orange-700">Back to Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = LucideIcons[service.icon] || LucideIcons.Circle;

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-56 pb-16 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/services" className="inline-flex items-center text-orange-600 hover:text-orange-700 mb-6">
              <LucideIcons.ArrowLeft className="w-5 h-5 mr-2" />
              Back to Services
            </Link>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-orange-600 rounded-xl flex items-center justify-center">
                <IconComponent className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900">{service.title}</h1>
            </div>
            <p className="text-2xl text-gray-600">{service.description}</p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {service.images.map((img, idx) => (
              <img 
                key={img}
                src={img}
                alt={`${service.title} ${idx + 1}`}
                className="w-full h-64 object-cover rounded-xl shadow-lg"
              />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">About This Service</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">{service.longDescription}</p>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-4">What We Offer</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {service.features.map((feature) => (
                <div key={feature} className="flex items-start space-x-3">
                  <LucideIcons.CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid md:grid-cols-5 gap-6">
              {service.process.map((item, idx) => (
                <Card key={item.step} className="text-center border-2 hover:border-orange-600 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-white">{idx + 1}</span>
                    </div>
                    <CardTitle className="text-lg">{item.step}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote for your {service.title.toLowerCase()} project
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8">
              <LucideIcons.Calendar className="w-5 h-5 mr-2" />
              Request Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;